import React, { useState, useRef, useEffect } from "react";

import type { FontFamilyValues } from "@excalidraw/element/types";

import { FontPreview } from "./FontPreview";

interface FontPreviewTooltipProps {
  fontFamily: FontFamilyValues;
  text: string;
  children: React.ReactNode;
  disabled?: boolean;
}

export const FontPreviewTooltip: React.FC<FontPreviewTooltipProps> = ({
  fontFamily,
  text,
  children,
  disabled = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = (event: MouseEvent) => {
    if (!triggerRef.current || !tooltipRef.current) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = event.clientX + 10;
    let y = event.clientY - tooltipRect.height / 2;

    // Adjust if tooltip would go off screen
    if (x + tooltipRect.width > viewportWidth) {
      x = event.clientX - tooltipRect.width - 10;
    }

    if (y < 0) {
      y = 10;
    } else if (y + tooltipRect.height > viewportHeight) {
      y = viewportHeight - tooltipRect.height - 10;
    }

    setPosition({ x, y });
  };

  const handleMouseEnter = (event: React.MouseEvent) => {
    if (disabled) {
      return;
    }
    setIsVisible(true);
    updatePosition(event.nativeEvent);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (disabled || !isVisible) {
      return;
    }
    updatePosition(event.nativeEvent);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      // Force a position update after the tooltip is rendered
      const rect = tooltipRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        // Tooltip is now rendered, we can update position if needed
      }
    }
  }, [isVisible]);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ display: "contents" }}
      >
        {children}
      </div>
      {isVisible && (
        <div
          ref={tooltipRef}
          className="font-preview-tooltip"
          style={{
            position: "fixed",
            left: `${position.x}px`,
            top: `${position.y}px`,
          }}
        >
          <FontPreview fontFamily={fontFamily} text={text} size={16} />
        </div>
      )}
    </>
  );
};
